/**
 * Advanced AI Engine for Zolaid IDE
 *
 * Features:
 * - Code scanning & analysis
 * - Smart suggestions & autocomplete
 * - Test generation
 * - Refactoring recommendations
 * - Performance analysis
 * - Security analysis
 * - Multi-file understanding
 * - Terminal integration
 */

export class AdvancedAIEngine {
  constructor() {
    this.codebaseContext = new Map();
    this.suggestions = [];
    this.analysisCache = new Map();
  }

  /**
   * Scan code structure for deep understanding
   */
  async scanCodeStructure(fileContent, filePath) {
    const cache = this.analysisCache.get(filePath);
    if (cache && Date.now() - cache.timestamp < 5000) return cache.data;

    const analysis = {
      functions: this._extractFunctions(fileContent),
      classes: this._extractClasses(fileContent),
      imports: this._extractImports(fileContent),
      exports: this._extractExports(fileContent),
      dependencies: this._extractDependencies(fileContent),
      complexity: this._calculateComplexity(fileContent),
      potentialIssues: this._findPotentialIssues(fileContent),
      testCoverage: this._estimateTestCoverage(fileContent),
      securityIssues: this._findSecurityIssues(fileContent),
      performanceIssues: this._findPerformanceIssues(fileContent),
      timestamp: Date.now(),
    };

    this.analysisCache.set(filePath, { data: analysis, timestamp: Date.now() });
    return analysis;
  }

  /**
   * Generate smart code suggestions at the editor position
   */
  generateSuggestions(context, position, fileContent) {
    const suggestions = [];
    const line = fileContent.split('\n')[position.line - 1] || '';
    const beforeCursor = line.substring(0, position.column);

    // Completion suggestions
    if (beforeCursor.endsWith('.')) {
      suggestions.push(...this._getPropertyCompletions(context, beforeCursor));
    }

    // Function call suggestions
    if (beforeCursor.includes('(') && !beforeCursor.includes(')')) {
      suggestions.push(...this._getFunctionParamSuggestions(context, beforeCursor));
    }

    // Import suggestions
    if (beforeCursor.includes('import')) {
      suggestions.push(...this._getImportSuggestions(context, beforeCursor));
    }

    // Snippet suggestions
    if (beforeCursor.endsWith('for ')) {
      suggestions.push(this._getForLoopSnippet());
    }
    if (beforeCursor.endsWith('if ')) {
      suggestions.push(this._getIfSnippet());
    }
    if (beforeCursor.endsWith('try ')) {
      suggestions.push(this._getTrySnippet());
    }

    return suggestions;
  }

  /**
   * Generate test code (like Claude Code)
   */
  async generateTests(functionCode, language = 'javascript') {
    const funcName = this._extractFunctionName(functionCode);
    const params = this._extractParameters(functionCode);
    const returnType = this._inferReturnType(functionCode);

    if (language === 'javascript' || language === 'typescript') {
      return `
import { describe, it, expect, beforeEach } from 'vitest';
import { ${funcName} } from './module';

describe('${funcName}', () => {
  let result;

  beforeEach(() => {
    // Setup
  });

  it('should handle normal input', () => {
    const input = ${this._generateMockInput(params)};
    result = ${funcName}(input);
    expect(result).toBeDefined();
  });

  it('should handle edge cases', () => {
    expect(() => ${funcName}(null)).toThrow();
    expect(() => ${funcName}(undefined)).toThrow();
  });

  it('should return correct type', () => {
    const input = ${this._generateMockInput(params)};
    result = ${funcName}(input);
    expect(typeof result).toBe('${this._inferJSType(returnType)}');
  });

  it('should handle errors gracefully', () => {
    expect(() => ${funcName}({})).not.toThrow();
  });
});
`;
    }

    return '// Test generation for this language not yet supported';
  }

  /**
   * Suggest refactoring improvements (Claude Code style)
   */
  getRefactoringMethods(codeContent) {
    const suggestions = [];

    // Extract method suggestions
    if (this._hasLongFunction(codeContent)) {
      suggestions.push({
        type: 'extract-method',
        title: '📌 Extract method - Code too long',
        description: 'This function is too long and does multiple things. Consider extracting into smaller functions.',
        priority: 'high',
      });
    }

    // Naming improvements
    if (this._hasPoorVariableNames(codeContent)) {
      suggestions.push({
        type: 'rename-variables',
        title: '📝 Improve variable names',
        description: 'Use more descriptive variable names (avoid x, y, temp, etc).',
        priority: 'medium',
      });
    }

    // DRY violations
    if (this._hasDuplicateCode(codeContent)) {
      suggestions.push({
        type: 'dry-violation',
        title: '🔁 Remove duplication',
        description: 'This code is repeated. Consider creating a utility function.',
        priority: 'high',
      });
    }

    // Complexity reduction
    if (this._hasHighCyclomaticComplexity(codeContent)) {
      suggestions.push({
        type: 'reduce-complexity',
        title: '⚡ Reduce complexity',
        description: 'High cyclomatic complexity. Use early returns or guard clauses.',
        priority: 'medium',
      });
    }

    // Error handling
    if (!this._hasErrorHandling(codeContent)) {
      suggestions.push({
        type: 'add-error-handling',
        title: '🛡️ Add error handling',
        description: 'This code lacks try-catch blocks or error checks.',
        priority: 'medium',
      });
    }

    // Type safety
    if (this._lacksTypeAnnotations(codeContent)) {
      suggestions.push({
        type: 'add-types',
        title: '📋 Add type annotations',
        description: 'Add JSDoc or TypeScript types for better IDE support.',
        priority: 'low',
      });
    }

    return suggestions;
  }

  /**
   * Deep analysis for debugging (Claude Code style)
   */
  async analyzeForBugs(fileContent, relatedFiles = []) {
    const bugs = [];

    // Memory leaks
    if (fileContent.includes('addEventListener') && !fileContent.includes('removeEventListener')) {
      bugs.push({
        type: 'memory-leak',
        severity: 'high',
        message: '⚠️ Potential memory leak: addEventListener without removeEventListener',
        line: fileContent.split('\n').findIndex(l => l.includes('addEventListener')),
        fix: 'Add cleanup: removeEventListener or use event delegation',
      });
    }

    // Race conditions
    if (fileContent.includes('async') && fileContent.includes('setState')) {
      bugs.push({
        type: 'race-condition',
        severity: 'high',
        message: '⚠️ Potential race condition: async operation with setState',
        fix: 'Use useCallback with dependencies or AbortController',
      });
    }

    // Null/undefined checks
    const unsafeAccess = this._findUnsafePropertyAccess(fileContent);
    unsafeAccess.forEach(line => {
      bugs.push({
        type: 'unsafe-access',
        severity: 'medium',
        message: '⚠️ Potential null/undefined access',
        line,
        fix: 'Add null checks or use optional chaining (?.)',
      });
    });

    // Infinite loops
    if (fileContent.includes('while(true)') || fileContent.includes('for(;;)')) {
      bugs.push({
        type: 'infinite-loop',
        severity: 'high',
        message: '⚠️ Infinite loop detected',
        fix: 'Add break condition or use setTimeout/setTimeout',
      });
    }

    // Type errors
    const typeErrors = this._findTypeErrors(fileContent);
    bugs.push(...typeErrors);

    // Security issues
    if (fileContent.includes('eval(')) {
      bugs.push({
        type: 'security',
        severity: 'critical',
        message: '🔴 CRITICAL: eval() is extremely dangerous',
        fix: 'Use Function constructor or other alternatives',
      });
    }

    if (fileContent.includes('innerHTML')) {
      bugs.push({
        type: 'xss',
        severity: 'high',
        message: '⚠️ innerHTML vulnerable to XSS attacks',
        fix: 'Use textContent, innerText, or sanitize HTML',
      });
    }

    return bugs;
  }

  /**
   * Performance profiling suggestions
   */
  analyzePerformance(fileContent) {
    const issues = [];

    // Unnecessary re-renders (React)
    if (fileContent.includes('useState') && !fileContent.includes('useMemo')) {
      issues.push({
        type: 'unnecessary-rerenders',
        severity: 'medium',
        message: '⚡ Consider memoizing expensive computations',
        suggestion: 'Use useMemo or useCallback for expensive operations',
      });
    }

    // Large loops
    const largeLoops = this._findLargeLoops(fileContent);
    if (largeLoops.length > 0) {
      issues.push({
        type: 'large-loops',
        severity: 'medium',
        message: '⚡ Large loops found - consider pagination or virtualization',
        lines: largeLoops,
      });
    }

    // Synchronous operations
    if (fileContent.includes('fetch') && !fileContent.includes('async')) {
      issues.push({
        type: 'blocking-operations',
        severity: 'medium',
        message: '⚡ Use async/await for non-blocking operations',
      });
    }

    return issues;
  }

  // Helper methods
  _extractFunctions(content) {
    const regex = /(?:function|const|let|var)\s+(\w+)\s*(?:=\s*)?(?:async\s+)?\(([^)]*)\)/g;
    const functions = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      functions.push({ name: match[1], params: match[2] });
    }
    return functions;
  }

  _extractClasses(content) {
    const regex = /class\s+(\w+)(?:\s+extends\s+(\w+))?\s*{/g;
    const classes = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      classes.push({ name: match[1], extends: match[2] || null });
    }
    return classes;
  }

  _extractImports(content) {
    const regex = /import\s+(?:{([^}]*)}|(\w+))\s+from\s+['"]([^'"]+)['"]/g;
    const imports = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      imports.push({ items: match[1] || match[2], source: match[3] });
    }
    return imports;
  }

  _extractExports(content) {
    const regex = /export\s+(?:default\s+)?(?:class|function|const|let|var)\s+(\w+)/g;
    const exports = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      exports.push(match[1]);
    }
    return exports;
  }

  _extractDependencies(content) {
    const imports = this._extractImports(content);
    return imports.map(i => i.source);
  }

  _calculateComplexity(content) {
    const conditions = (content.match(/if|else|switch|case|for|while|catch/g) || []).length;
    return {
      cyclomatic: conditions + 1,
      lines: content.split('\n').length,
      level: conditions > 10 ? 'high' : conditions > 5 ? 'medium' : 'low',
    };
  }

  _findPotentialIssues(content) {
    const issues = [];
    if (!content.includes('try') || !content.includes('catch')) issues.push('Missing error handling');
    if (content.includes('TODO') || content.includes('FIXME')) issues.push('Contains TODO/FIXME comments');
    if (content.includes('console.log') && !content.includes('//')) issues.push('Console logs in production code');
    return issues;
  }

  _estimateTestCoverage(content) {
    const hasTests = content.includes('describe') || content.includes('test') || content.includes('it(');
    return hasTests ? 'partial' : 'none';
  }

  _findSecurityIssues(content) {
    const issues = [];
    if (content.includes('eval(')) issues.push('eval() usage - extremely dangerous');
    if (content.includes('innerHTML')) issues.push('innerHTML - XSS vulnerability');
    if (content.includes('JSON.parse') && !content.includes('try')) issues.push('JSON.parse without try-catch');
    return issues;
  }

  _findPerformanceIssues(content) {
    const issues = [];
    if (content.includes('setTimeout') && content.includes('0')) issues.push('Heavy setTimeout usage');
    if ((content.match(/\[.*for.*/g) || []).length > 2) issues.push('Multiple nested loops - O(n²) complexity');
    return issues;
  }

  _hasLongFunction(content) {
    const lines = content.split('\n').length;
    return lines > 50;
  }

  _hasPoorVariableNames(content) {
    return /\b[xyz]\s*=|let\s+[xyz]\b/.test(content);
  }

  _hasDuplicateCode(content) {
    const lines = content.split('\n');
    const lineSet = new Set();
    for (const line of lines) {
      if (lineSet.has(line)) return true;
      lineSet.add(line);
    }
    return false;
  }

  _hasHighCyclomaticComplexity(content) {
    const complexity = this._calculateComplexity(content);
    return complexity.cyclomatic > 15;
  }

  _hasErrorHandling(content) {
    return /try|catch|throw|Error|reject/.test(content);
  }

  _lacksTypeAnnotations(content) {
    return !/:\s*\w+|\/\*\*[\s\S]*?\*\/|@param|@returns/.test(content);
  }

  _findUnsafePropertyAccess(content) {
    const lines = [];
    content.split('\n').forEach((line, i) => {
      if (/\w+\.\w+\.\w+/.test(line) && !line.includes('?.')) {
        lines.push(i);
      }
    });
    return lines;
  }

  _findTypeErrors(content) {
    const errors = [];
    // Simulate type checking
    if (content.includes('+') && (content.includes('null') || content.includes('undefined'))) {
      errors.push({
        type: 'type-error',
        severity: 'medium',
        message: '⚠️ Possible type coercion issue with null/undefined',
      });
    }
    return errors;
  }

  _findLargeLoops(content) {
    const lines = [];
    content.split('\n').forEach((line, i) => {
      if (/for\s*\(|while\s*\(/.test(line)) lines.push(i);
    });
    return lines;
  }

  _getPropertyCompletions(context, beforeCursor) {
    return [
      { label: 'length', type: 'property' },
      { label: 'toString()', type: 'method' },
      { label: 'valueOf()', type: 'method' },
    ];
  }

  _getFunctionParamSuggestions(context, beforeCursor) {
    return [{ label: 'callback', type: 'parameter' }];
  }

  _getImportSuggestions(context, beforeCursor) {
    return [
      { label: 'React', source: 'react' },
      { label: 'useState', source: 'react' },
    ];
  }

  _getForLoopSnippet() {
    return {
      label: 'for loop',
      snippet: 'for (let i = 0; i < array.length; i++) {\n  // code\n}',
      type: 'snippet',
    };
  }

  _getIfSnippet() {
    return {
      label: 'if statement',
      snippet: 'if (condition) {\n  // code\n}',
      type: 'snippet',
    };
  }

  _getTrySnippet() {
    return {
      label: 'try-catch',
      snippet: 'try {\n  // code\n} catch (error) {\n  // handle error\n}',
      type: 'snippet',
    };
  }

  _extractFunctionName(code) {
    const match = code.match(/function\s+(\w+)|const\s+(\w+)\s*=|(\w+)\s*:/);
    return match ? match[1] || match[2] || match[3] : 'unknown';
  }

  _extractParameters(code) {
    const match = code.match(/\(([^)]*)\)/);
    return match ? match[1].split(',').map(p => p.trim()) : [];
  }

  _inferReturnType(code) {
    if (code.includes('return') && code.includes('[')) return 'array';
    if (code.includes('return') && code.includes('{')) return 'object';
    if (code.includes('return true') || code.includes('return false')) return 'boolean';
    return 'void';
  }

  _generateMockInput(params) {
    if (params.length === 0) return '{}';
    return params[0]?.includes('=') ? params[0].split('=')[0].trim() : '{}';
  }

  _inferJSType(type) {
    const typeMap = { array: 'object', object: 'object', boolean: 'boolean', string: 'string', number: 'number' };
    return typeMap[type] || 'object';
  }
}

export default AdvancedAIEngine;
